import { useState, useRef, useEffect } from 'react';

const NAVY = '#0C2D48';
const BORDER = '#e0e0e0';
const FONT = "'Raleway', sans-serif";

const FIELD_TYPES = [
  { value: 'TEXT',     label: 'Text' },
  { value: 'NUMBER',   label: 'Number' },
  { value: 'DATE',     label: 'Date' },
  { value: 'SELECT',   label: 'Select' },
  { value: 'CHECKBOX', label: 'Checkbox' },
  { value: 'URL',      label: 'URL' },
  { value: 'USER',     label: 'User' },
];

const OPTION_COLORS = ['#6366f1','#10b981','#f59e0b','#f97316','#ef4444','#8b5cf6','#3b82f6','#94a3b8'];

const s = {
  row: {
    display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
    padding: '0.6rem 0.75rem', background: '#fff',
    border: `1px solid ${BORDER}`, borderRadius: 8, marginBottom: 6,
    fontFamily: FONT, flexWrap: 'wrap',
  },
  input: {
    fontFamily: FONT, fontSize: '0.84rem', padding: '0.35rem 0.5rem',
    border: `1px solid ${BORDER}`, borderRadius: 6, color: NAVY, outline: 'none', flex: 1,
  },
  select: {
    fontFamily: FONT, fontSize: '0.84rem', padding: '0.35rem 0.4rem',
    border: `1px solid ${BORDER}`, borderRadius: 6, color: NAVY,
    background: '#fff', cursor: 'pointer', outline: 'none',
  },
  label: {
    fontSize: '0.7rem', fontWeight: 700, color: '#888', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: 2, display: 'block',
  },
  field: { display: 'flex', flexDirection: 'column', gap: 2 },
  toggle: (on) => ({
    width: 30, height: 16, borderRadius: 8, border: 'none', cursor: 'pointer',
    background: on ? '#6366f1' : '#d1d5db', position: 'relative',
    transition: 'background 0.2s', flexShrink: 0,
  }),
  toggleThumb: (on) => ({
    position: 'absolute', top: 1.5, left: on ? 13 : 1.5, width: 13, height: 13,
    borderRadius: '50%', background: '#fff', transition: 'left 0.2s',
  }),
  deleteBtn: {
    background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444',
    fontSize: '1rem', padding: '0.2rem', flexShrink: 0, alignSelf: 'flex-start', marginTop: 18,
  },
  chip: (colour) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4,
    background: colour + '22', border: `1px solid ${colour}55`,
    borderRadius: 12, padding: '0.1rem 0.4rem', fontSize: '0.75rem', color: NAVY,
  }),
  chipClose: {
    background: 'none', border: 'none', cursor: 'pointer', color: '#888',
    fontSize: '0.8rem', padding: 0, lineHeight: 1,
  },
  addOptBtn: {
    background: 'none', border: `1px dashed ${BORDER}`, cursor: 'pointer',
    borderRadius: 12, padding: '0.1rem 0.5rem', fontSize: '0.75rem',
    color: '#6366f1', fontFamily: FONT,
  },
};

export default function ColumnRow({ column, onChange, onDelete, readOnly }) {
  const [name, setName]           = useState(column.name      || '');
  const [fieldKey, setKey]        = useState(column.fieldKey  || '');
  const [fieldType, setType]      = useState(column.fieldType || 'TEXT');
  const [options, setOptions]     = useState(Array.isArray(column.options) ? column.options : []);
  const [isRequired, setRequired] = useState(column.isRequired ?? false);
  const [addingOpt, setAddingOpt] = useState(false);
  const [optLabel, setOptLabel]   = useState('');
  const [optPickerIdx, setOptPickerIdx] = useState(null);
  const optColorIdx               = useRef(0);
  const nameRef                   = useRef(null);
  const optPickerRef              = useRef(null);

  useEffect(() => {
    if (optPickerIdx === null) return;
    function handler(e) {
      if (optPickerRef.current && !optPickerRef.current.contains(e.target)) setOptPickerIdx(null);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [optPickerIdx]);

  function emit(patch) {
    if (onChange) onChange({ name, fieldKey, fieldType, options, isRequired, ...patch });
  }

  function handleNameBlur() {
    // Auto-generate fieldKey from name if empty
    const key = fieldKey || name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    setKey(key);
    emit({ name, fieldKey: key });
  }

  function addOption() {
    if (!optLabel.trim()) return;
    const colour = OPTION_COLORS[optColorIdx.current % OPTION_COLORS.length];
    optColorIdx.current += 1;
    const newOpts = [...options, { label: optLabel.trim(), value: optLabel.trim().toLowerCase().replace(/\s+/g,'_'), colour }];
    setOptions(newOpts);
    setOptLabel('');
    setAddingOpt(false);
    emit({ options: newOpts });
  }

  function removeOption(idx) {
    const newOpts = options.filter((_, i) => i !== idx);
    setOptions(newOpts);
    emit({ options: newOpts });
  }

  return (
    <div style={s.row}>
      {/* Field name */}
      <div style={{ ...s.field, minWidth: 120 }}>
        <span style={s.label}>Field name</span>
        <input
          ref={nameRef}
          style={s.input}
          value={name}
          placeholder="e.g. Deal value"
          disabled={readOnly}
          onChange={e => setName(e.target.value)}
          onBlur={handleNameBlur}
        />
      </div>

      {/* Field key (auto-filled, editable) */}
      <div style={{ ...s.field, minWidth: 100 }}>
        <span style={s.label}>Key</span>
        <input
          style={{ ...s.input, color: '#888', fontSize: '0.78rem' }}
          value={fieldKey}
          placeholder="field_key"
          disabled={readOnly}
          onChange={e => setKey(e.target.value)}
          onBlur={() => emit({ fieldKey })}
        />
      </div>

      {/* Field type */}
      <div style={s.field}>
        <span style={s.label}>Type</span>
        <select
          style={s.select}
          value={fieldType}
          disabled={readOnly}
          onChange={e => { setType(e.target.value); emit({ fieldType: e.target.value }); }}
        >
          {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      {/* SELECT options */}
      {fieldType === 'SELECT' && (
        <div style={{ ...s.field, width: '100%', marginTop: 4 }}>
          <span style={s.label}>Options</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }} ref={optPickerRef}>
            {options.map((opt, i) => (
              <span key={i} style={{ ...s.chip(opt.colour || '#94a3b8'), position: 'relative' }}>
                <span
                  style={{ width: 8, height: 8, borderRadius: '50%', background: opt.colour || '#94a3b8', display: 'inline-block', cursor: !readOnly ? 'pointer' : 'default', flexShrink: 0 }}
                  title="Change colour"
                  onClick={() => !readOnly && setOptPickerIdx(optPickerIdx === i ? null : i)}
                />
                {optPickerIdx === i && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '0.5rem', display: 'flex', gap: 6, flexWrap: 'wrap', width: 128, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: 2 }}>
                    {OPTION_COLORS.map(c => (
                      <div
                        key={c}
                        style={{ width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer', border: c === (opt.colour || '#94a3b8') ? '2.5px solid #0C2D48' : '2.5px solid transparent', boxSizing: 'border-box' }}
                        onClick={() => {
                          const newOpts = options.map((o, j) => j === i ? { ...o, colour: c } : o);
                          setOptions(newOpts);
                          setOptPickerIdx(null);
                          emit({ options: newOpts });
                        }}
                      />
                    ))}
                  </div>
                )}
                {opt.label}
                {!readOnly && (
                  <button style={s.chipClose} onClick={() => removeOption(i)}>×</button>
                )}
              </span>
            ))}

            {!readOnly && !addingOpt && (
              <button style={s.addOptBtn} onClick={() => setAddingOpt(true)}>+ Add option</button>
            )}

            {!readOnly && addingOpt && (
              <span style={{ display: 'inline-flex', gap: 4 }}>
                <input
                  autoFocus
                  style={{ ...s.input, flex: 'none', width: 100, fontSize: '0.78rem' }}
                  value={optLabel}
                  placeholder="Option name"
                  onChange={e => setOptLabel(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addOption(); if (e.key === 'Escape') setAddingOpt(false); }}
                />
                <button style={{ ...s.addOptBtn, borderStyle: 'solid' }} onClick={addOption}>✓</button>
                <button style={{ ...s.addOptBtn, color: '#888' }} onClick={() => setAddingOpt(false)}>✕</button>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Required toggle */}
      <div style={{ ...s.field, alignItems: 'center', marginTop: 18 }}>
        <span style={{ ...s.label, marginBottom: 4 }}>Required</span>
        <button
          style={s.toggle(isRequired)}
          disabled={readOnly}
          onClick={() => { const v = !isRequired; setRequired(v); emit({ isRequired: v }); }}
        >
          <div style={s.toggleThumb(isRequired)} />
        </button>
      </div>

      {/* Delete */}
      {!readOnly && (
        <button style={s.deleteBtn} title="Delete column" onClick={onDelete}>🗑</button>
      )}
    </div>
  );
}
